import { Visibility } from '@mui/icons-material'
import { Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { EditButton, GreenText, PhotoCard, ViewPhotoDialog } from 'components'
import { savePhotos } from 'controllers/outboundTrips/index'
import { OutboundTripDetailDto, OutboundTripDetailPhotosDto } from 'dtos/outboundTrips'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import UploadReplaceDialog from './UploadReplaceDialog'

interface PhotosCardProp {
  outboundTripGuid?: string
  photos?: OutboundTripDetailPhotosDto
  setState: (outboundTripDetail: OutboundTripDetailDto) => void
}

export default function PhotosCard({ outboundTripGuid, photos, setState }: PhotosCardProp) {
  const [editDialoOpen, setEditDialogOpen] = useState<boolean>(false)
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  return (
    <>
      <UploadReplaceDialog
        initialValues={photos ?? { driversLicensePhotoFile: undefined }}
        isLoading={isSaving}
        onClose={() => setEditDialogOpen(false)}
        onSave={values => {
          setIsSaving(true)
          savePhotos(outboundTripGuid!, values)
            .then(res => {
              enqueueSnackbar('Outbound Trip Photos Saved Successfully!', { variant: 'success' })
              setState({ photos: res.value })
              setEditDialogOpen(false)
            })
            .finally(() => setIsSaving(false))
        }}
        open={editDialoOpen}
      />

      {photos?.driversLicensePhotoFileName && (
        <ViewPhotoDialog fileName={photos.driversLicensePhotoFileName} onClose={() => setViewDialogOpen(false)} open={viewDialogOpen} />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h6'>
                  Outbound Trip Photos
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto' container spacing={1} alignItems='flex-end'>
                <Grid item xs={12} sm='auto'>
                  <Button
                    color='primary'
                    disabled={!photos?.driversLicensePhotoFileName}
                    endIcon={<Visibility />}
                    onClick={() => setViewDialogOpen(true)}
                    variant='text'
                  >
                    VIEW SELECTED
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <EditButton onEdit={() => setEditDialogOpen(true)} text='UPLOAD/REPLACE SELECTED' />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} container justifyContent='center'>
              <Grid item>
                <PhotoCard
                  complete={Boolean(photos?.driversLicensePhotoFileName)}
                  label={`Driver's License Photo`}
                  src={photos?.driversLicensePhotoFileName ? `/api/documents/${encodeURIComponent(photos.driversLicensePhotoFileName)}/preview` : undefined}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**VIEW SELECTED Button:**

The “VIEW SELECTED” button is only available if the user has selected photos to view.

Clicking the “VIEW SELECTED” button will open the View Check In Photo Dialog.

**Driver’s License Photo:**

Displays the Drivers License Photo image.

This Photo is uploaded by users via the “Needs Driver’s License Uploaded” To-Do List.

Once uploaded, the image can be replaced using the “Upload/Replace Selected” link.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
